import React from "react";
import { Pencil, Trash2 } from "lucide-react";
import { IOwnerPropertyOption, ISubAccount } from "./types";

interface SubAccountCardProps {
  account: ISubAccount;
  properties: IOwnerPropertyOption[];
  isUpdating: boolean;
  onEdit: (account: ISubAccount) => void;
  onToggleActive: (account: ISubAccount) => void;
  onDelete: (account: ISubAccount) => void;
}

export default function SubAccountCard({
  account,
  properties,
  isUpdating,
  onEdit,
  onToggleActive,
  onDelete,
}: SubAccountCardProps) {
  const linkedProperties = properties.filter((property) =>
    account.property_ids.includes(property.id)
  );

  return (
    <article className="flex flex-col gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-bold text-[#2D3748]">{account.first_name}</h3>
          <span
            className={`rounded-full px-2.5 py-0.5 text-[0.65rem] font-semibold ${
              account.active
                ? "bg-emerald-50 text-emerald-700"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            {account.active ? "Ativo" : "Inativo"}
          </span>
        </div>
        <p className="truncate text-sm text-[#475569]">{account.email}</p>
        {account.phone && (
          <p className="text-sm text-[#64748B]">{account.phone}</p>
        )}
        <p
          className="truncate text-xs text-[#64748B]"
          title={
            linkedProperties.length > 0
              ? linkedProperties.map((property) => property.title).join(", ")
              : undefined
          }
        >
          Imóveis:{" "}
          {linkedProperties.length > 0
            ? linkedProperties.map((property) => property.title).join(", ")
            : `${account.property_ids.length} vinculado(s)`}
        </p>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onToggleActive(account)}
          disabled={isUpdating}
          className="rounded-full border border-[#CBD5E1] px-3 py-2 text-xs font-semibold text-[#475569] transition-colors hover:bg-[#F8FAFC] disabled:opacity-60"
        >
          {account.active ? "Desativar" : "Ativar"}
        </button>
        <button
          type="button"
          onClick={() => onEdit(account)}
          disabled={isUpdating}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#DD6435]/40 px-3 py-2 text-xs font-semibold text-[#DD6435] transition-colors hover:bg-[#DD6435]/10 disabled:opacity-60"
        >
          <Pencil className="h-3.5 w-3.5" />
          Editar
        </button>
        <button
          type="button"
          onClick={() => onDelete(account)}
          disabled={isUpdating}
          className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remover
        </button>
      </div>
    </article>
  );
}
